import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Notify, Confirm } from 'notiflix';
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { calculatePriceRange, groupTiersByDurationCountry, getDefaultTierFilter } from 'utils/valuePack/valuePackUtils';
import { getCountryByCode } from 'utils/valuePack/countryUtils';
import { validateTiers } from 'schemas/valuePackSchema';
import { 
    selectCurrentValuePack, 
  updateValuePackTiersLocal,
  fetchValuePackDetails 
} from 'store/slices/valuePackSlice';
import { deleteValuePackTiersByCriteria } from 'store/slices/valuePackSlice';
import usePermission from 'hooks/usePermission';

const ValuePackTiers = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');

  const currentValuePack = useSelector(selectCurrentValuePack);
  // The store keeps the last opened pack; only use it when it is the one in the URL.
  const valuePack =
    currentValuePack && String(currentValuePack.id) === String(id) ? currentValuePack : null;

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [tierGroups, setTierGroups] = useState({});
  const [validationError, setValidationError] = useState('');
  const [filterDuration, setFilterDuration] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const defaultFiltersApplied = useRef(false);

  useEffect(() => {
    if (defaultFiltersApplied.current) return;
    if (!tiers.length) return;

    defaultFiltersApplied.current = true;
    const defaults = getDefaultTierFilter(tiers);
    setFilterCountry(defaults.country);
    setFilterDuration(defaults.duration);
  }, [tiers]);

  const transformTiersForValidation = (tiers) => {
    return tiers.map(tier => {
      const countryCode = tier.countryCode;
      const countryData = getCountryByCode(countryCode);
      const currency = countryData?.currency || 'USD';
      
      return {
        ...tier,
        countryPricing: {
          [countryCode]: {
            price: tier.currentPrice || tier.originalPrice,
            currency: currency,
            discount: tier.discountValue || 0,
            originalPrice: tier.originalPrice
          }
        }
      };
    });
  };

  const loadValuePack = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!valuePack) {
        await dispatch(fetchValuePackDetails(id)).unwrap();
      } else {
        const packTiers = valuePack.tiers || [];
        setTiers(packTiers);
        
        const groups = groupTiersByDurationCountry(packTiers);
        setTierGroups(groups);
        
        if (packTiers.length > 0) {
          try {
            const transformedTiers = transformTiersForValidation(packTiers);
            await validateTiers({ tiers: transformedTiers });
            setValidationError('');
          } catch (error) {
            const errorMessage = error.errors ? error.errors.join(', ') : error.message;
            setValidationError(errorMessage);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load value pack:', error);
      Notify.failure('Failed to load value pack tiers');
      navigate('/value-pack');
    } finally {
      setLoading(false);
    }
  }, [id, valuePack, dispatch, navigate]);

  useEffect(() => {
    loadValuePack();
  }, [loadValuePack]);

  useEffect(() => {
    if (valuePack && valuePack.tiers) {
      const packedTiers = valuePack.tiers || [];
      setTiers(packedTiers);
      const groups = groupTiersByDurationCountry(packedTiers);
      setTierGroups(groups);
    }
  }, [valuePack]);

  const handleEditTierGroup = useCallback((durationCountryKey) => {
    const group = tierGroups[durationCountryKey];
    
    if (!group || !group.tiers || group.tiers.length === 0) {
      Notify.failure("Tier group not found");
      return;
    }
    
    // first extract durationId and countryId from tier
    const firstTier = group.tiers[0];
    const durationId = firstTier.durationId;
    const countryId = firstTier.countryId;
    
    // send durationId and countryId in URL
    navigate(`/value-pack/${id}/tiers/edit/${durationId}/${countryId}`);
  }, [tierGroups, navigate, id]);

  const handleDeleteTierGroup = useCallback((durationCountryKey) => {
    // Same fix: Use setState callback pattern
    setTierGroups(currentGroups => {
      const group = currentGroups[durationCountryKey];
      if (!group) return currentGroups;

      const countryData = getCountryByCode(group.country);
      
      Confirm.show(
        'Delete Tier Group',
        `Are you sure you want to delete all tiers for ${group.duration} month${group.duration > 1 ? 's' : ''} duration in ${countryData?.name || group.country}? This action cannot be undone.`,
        'Delete',
        'Cancel',
        async () => {
          try {
            await dispatch(deleteValuePackTiersByCriteria({
              valuePackId: id,
              duration: group.duration,
              countryCode: group.country
            })).unwrap();
            
            const updatedTiers = tiers.filter(tier => 
              !(tier.duration === group.duration && tier.countryCode === group.country)
            );
            
            setTiers(updatedTiers);
            
            const groups = groupTiersByDurationCountry(updatedTiers);
            setTierGroups(groups);
            
            if (updatedTiers.length > 0) {
              try {
                await validateTiers({ tiers: updatedTiers });
                setValidationError('');
              } catch (error) {
                const errorMessage = error.errors ? error.errors.join(', ') : error.message;
                setValidationError(errorMessage);
              }
            } else {
              setValidationError('');
            }
            
            Notify.success('Tier group deleted successfully');
          } catch (error) {
            console.error('Failed to delete tier group:', error);
            Notify.failure('Failed to delete tier group');
          }
        }
      );

      return currentGroups;
    });
  }, [tiers, id, dispatch]);

  const handleCreateNewTier = () => {
    navigate(`/value-pack/${id}/tiers/new`);
  };

  const availableDurations = useMemo(() => {
    const durations = new Set();
    Object.values(tierGroups).forEach((group) => durations.add(group.duration));
    return Array.from(durations).sort((a, b) => a - b);
  }, [tierGroups]);

  const availableCountries = useMemo(() => {
    const countries = new Map();
    Object.values(tierGroups).forEach((group) => {
      if (group.country && !countries.has(group.country)) {
        countries.set(group.country, getCountryByCode(group.country));
      }
    });
    return Array.from(countries.entries());
  }, [tierGroups]);

  const filteredGroupKeys = useMemo(() => {
    return Object.keys(tierGroups)
      .filter((key) => {
        const group = tierGroups[key];
        const matchesDuration =
          filterDuration === 'all' || String(group.duration) === String(filterDuration);
        const matchesCountry =
          filterCountry === 'all' || group.country === filterCountry;
        return matchesDuration && matchesCountry;
      })
      .sort();
  }, [tierGroups, filterDuration, filterCountry]);

  const renderDurationCountryFilter = () => (
    <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Duration</InputLabel>
        <Select
          label="Duration"
          value={filterDuration}
          onChange={(e) => setFilterDuration(e.target.value)}
        >
          <MenuItem value="all">All Durations</MenuItem>
          {availableDurations.map((duration) => (
            <MenuItem key={duration} value={duration}>
              {duration} Month{duration > 1 ? 's' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Country</InputLabel>
        <Select
          label="Country"
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
        >
          <MenuItem value="all">All Countries</MenuItem>
          {availableCountries.map(([code, countryData]) => (
            <MenuItem key={code} value={code}>
              {countryData?.name || code} ({code})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const renderTierGroupsTable = () => {
    const groupKeys = filteredGroupKeys;

    if (groupKeys.length === 0) {
      const hasAnyGroups = Object.keys(tierGroups).length > 0;
      return (
        <Box py={4} textAlign="center">
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {hasAnyGroups ? 'No tier configurations match the selected filters' : 'No tier configurations found'}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            {hasAnyGroups
              ? 'Try a different duration or country, or clear the filters'
              : 'Create your first tier configuration by selecting duration and country combination'}
          </Typography>
          {hasAnyGroups ? (
            <Button
              variant="outlined"
              onClick={() => {
                setFilterDuration('all');
                setFilterCountry('all');
              }}
            >
              Clear Filters
            </Button>
          ) : (
            canUpdate && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateNewTier}
                sx={{ color: "#fff" }}
              >
                Create First Tier Configuration
              </Button>
            )
          )}
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Duration & Country</TableCell>
              <TableCell>Configured Tiers</TableCell>
              <TableCell>Price Range</TableCell>
              <TableCell>Features</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupKeys.map((groupKey) => {
              const group = tierGroups[groupKey];
              const countryData = getCountryByCode(group.country);
              let priceRange = null;
              
              try {
                priceRange = calculatePriceRange(group.tiers, group.country);
              } catch (error) {
                console.error('Error calculating price range:', error);
                priceRange = { min: 0, max: 0, currency: 'USD' };
              }

              const totalFeatures = group.tiers.reduce((total, tier) => {
                return total + (tier.featuresConfiguration?.length || 0) + (tier.additionalFeatures?.length || 0);
              }, 0);

              return (
                <TableRow key={groupKey}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        {group.duration} Month{group.duration > 1 ? 's' : ''}
                      </Typography>
                      <Chip 
                        label={countryData?.name || group.country} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {group.tiers.map(tier => (
                        <Chip
                          key={tier.id}
                          label={tier.tierType}
                          size="small"
                          color={
                            tier.tierType === 'Low' ? 'info' :
                            tier.tierType === 'Mid' ? 'warning' : 'success'
                          }
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    {group.tiers.some(t => t.isPopular) && (
                      <Chip label="Has Popular Tier" color="secondary" size="small" sx={{ mt: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {priceRange && priceRange.min !== null && (
                      <Box>
                        <Typography variant="body2">
                          {countryData?.currency || priceRange.currency} {priceRange.min}
                          {priceRange.min !== priceRange.max && ` - ${priceRange.max}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {group.tiers.length} tier{group.tiers.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {totalFeatures} total features
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      across all tiers
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1}>
                      {canUpdate && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditTierGroup(groupKey)}
                          title="Edit this tier configuration"
                          sx={{ color: "#299cf9ff" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteTierGroup(groupKey)}
                          color="error"
                          title="Delete this tier configuration"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderDetailedTierView = () => {
    const groupKeys = filteredGroupKeys;
    
    return (
      <Grid container spacing={3}>
        {groupKeys.map(groupKey => {
          const group = tierGroups[groupKey];
          const countryData = getCountryByCode(group.country);
          
          return (
            <Grid item xs={12} key={groupKey}>
              <Card>
                <MDBox p={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {group.duration} Month{group.duration > 1 ? 's' : ''} - {countryData?.name || group.country}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {group.tiers.length} tier{group.tiers.length > 1 ? 's' : ''} configured
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    {group.tiers.map(tier => {
                      const pricing = tier?.currentPrice;
                      
                      return (
                        <Grid item xs={12} md={4} key={tier.id}>
                          <Card variant="outlined">
                            <MDBox p={2}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle1" color={
                                  tier.tierType === 'Low' ? 'info.main' :
                                  tier.tierType === 'Mid' ? 'warning.main' : 'success.main'
                                }>
                                  {tier.name || `${tier.tierType} Tier`}
                                </Typography>
                                {tier.isPopular && (
                                  <Chip label="Popular" color="secondary" size="small" />
                                )}
                              </Box>
                              
                              <Typography variant="body2" color="textSecondary" mb={2}>
                                {tier.description || 'No description'}
                              </Typography>
                              
                              {pricing && (
                                <>
                                  <Typography variant="h6" color="primary" gutterBottom>
                                    {countryData?.currency || "USD" } {pricing || 0}
                                    {pricing.discountValue > 0 && (
                                      <Typography component="span" variant="body2" sx={{ 
                                        textDecoration: 'line-through', 
                                        ml: 1, 
                                        color: 'text.secondary' 
                                      }}>
                                        {countryData?.currency || pricing.currency} {(pricing || 0) + (pricing.discount || 0)}
                                      </Typography>
                                    )}
                                  </Typography>
                                </>
                              )}
                              
                              <Divider sx={{ my: 1 }} />
                              
                              <Typography variant="caption" color="textSecondary" display="block">
                                Core Features: {tier.featuresConfiguration?.length || 0}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" display="block">
                                Premium Add-ons: {tier.additionalFeatures?.length || 0}
                              </Typography>
                            </MDBox>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </MDBox>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate(`/value-pack/edit/${id}`)}
                sx={{ color: '#fff', mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <MDTypography variant="h6" sx={{color:"#fff"}}>
                {valuePack?.name} - Manage Tiers
              </MDTypography>
            </Box>
            {canUpdate && (
              <Button
                variant="contained"
                color="light"
                onClick={handleCreateNewTier}
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Add New Tier Configuration
              </Button>
            )}
          </MDBox>

          <MDBox p={3}>
            {validationError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Validation Error</AlertTitle>
                {validationError}
              </Alert>
            )}
            
            {Object.keys(tierGroups).length > 0 && (
              <>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                  <Tab label="Overview" />
                  <Tab label="Detailed View" />
                  <Tab label="Configuration Guide" />
                </Tabs>
                <Divider sx={{ mb: 3 }} />
                {activeTab !== 2 && renderDurationCountryFilter()}
              </>
            )}

            {activeTab === 0 && renderTierGroupsTable()}
            
            {activeTab === 1 && Object.keys(tierGroups).length > 0 && renderDetailedTierView()}
            
            {activeTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      How Tier Configuration Works
                    </Typography>
                    <Typography variant="body2">
                      Each tier configuration is specific to a duration and country combination. 
                      You can create multiple configurations for different markets and subscription periods.
                    </Typography>
                  </Alert>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <MDBox p={3} textAlign="center">
                      <Typography variant="h6" color="info.main" gutterBottom>
                        Duration-Country Combinations
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Create different pricing structures for various markets and subscription periods.
                        Each combination can have its own Low, Mid, and High tier configuration.
                      </Typography>
                    </MDBox>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <MDBox p={3} textAlign="center">
                      <Typography variant="h6" color="warning.main" gutterBottom>
                        Flexible Tier Types
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Configure Low, Mid, and High tiers with different pricing, features, and discounts.
                        Mark popular tiers to highlight them to customers.
                      </Typography>
                    </MDBox>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Card>
                    <MDBox p={3} textAlign="center">
                      <Typography variant="h6" color="success.main" gutterBottom>
                        Feature Mapping
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Assign core and premium features to each tier level.
                        Create compelling value propositions across different price points.
                      </Typography>
                    </MDBox>
                  </Card>
                </Grid>
              </Grid>
            )}

            {Object.keys(tierGroups).length === 0 && activeTab === 0 && renderTierGroupsTable()}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default ValuePackTiers;
